import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import {
  Box,
  HStack,
  ScrollView,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { useInitEventForm } from "../../atoms/useInitEventForm";
import { useInitEventMeta } from "../../atoms/useInitEventMeta";
import { useInitEventSignUps } from "../../atoms/useInitEventSignUps";
import { useInitGroupEvent } from "../../atoms/useInitGroupEvent";
import { EventDetailRedesignedHeader } from "../EventDetailRedesignedHeader";
import { EventDetailQueryString } from "../EventDetailShell/EventDetailShell";
import { EventDetailShellQuery } from "../EventDetailShell/__generated__/EventDetailShellQuery.graphql";
import { EventDetailSignUpSection } from "../EventDetailSignUpSection";
import { NewEventDetailBody } from "../NewEventDetailBody";
import { UpdateAttendeesGroupEventsModal } from "../UpdateAttendeesGroupEvents";

type EventDetailWrapperProps = {
  queryRef: PreloadedQuery<EventDetailShellQuery>;
};

export const EventDetailWrapper = ({ queryRef }: EventDetailWrapperProps) => {
  const data = usePreloadedQuery(EventDetailQueryString, queryRef);
  const event = data.node?.__typename === "Event" ? data.node : null;
  const defaultPaymentCardOnFileId =
    data.session?.user?.squareCards?.edges?.find(
      (edge) => !!edge?.node?.default
    )?.node?.id ?? undefined;
  const insets = useSafeAreaInsets();

  useInitGroupEvent({ fragmentRef: data.node ?? null });
  useInitEventMeta({ fragmentRef: data.node ?? null });
  useInitEventForm({ fragmentRef: data.node ?? null });
  useInitEventSignUps({ fragmentRef: data.node ?? null });

  return data.node ? (
    <Box flex={1} justifyContent="center" alignItems="center">
      <Container
        marginTop={4}
        paddingHorizontal={{ base: 0, md: 16 }}
        height="100%"
        flex={1}
        style={{ paddingTop: insets.top }}
        testID="event-detail-wrapper"
      >
        {/* Header on mobile */}
        <Box visible={{ xs: true, lg: false }}>
          <EventDetailRedesignedHeader fragmentRef={data.node} />
        </Box>

        <ScrollView
          flex={1}
          height="100%"
          scrollEventThrottle={16}
          contentContainerStyle={{ paddingBottom: insets.bottom }}
        >
          <Box width="100%" maxWidth={1240} alignSelf="center">
            {/* Header on desktop - full width above both columns */}
            <Box
              visible={{
                xs: false,
                base: false,
                sm: false,
                md: false,
                lg: true,
              }}
            >
              <EventDetailRedesignedHeader fragmentRef={data.node} />
            </Box>

            <HStack
              flexDirection={{ base: "column", md: "column", lg: "row" }}
              gap={{ base: 0, lg: 24 }}
            >
              {/* LEFT Column (desktop): Image + Sign Up card + Attendees */}
              {/* On mobile this renders as a single stacked column */}
              <Box flex={{ lg: 1 }} width={{ base: "100%", lg: "auto" }}>
                {/* Desktop: image only in left column */}
                <Box
                  visible={{
                    xs: false,
                    base: false,
                    sm: false,
                    md: false,
                    lg: true,
                  }}
                  width="100%"
                >
                  <NewEventDetailBody fragmentRef={data.node} imageOnly />
                </Box>

                {/* Mobile: full body (image + info) */}
                {/* Attendees render after the sign up section via EventDetailSignUpSection */}
                <Box
                  visible={{ xs: true, lg: false }}
                  width="100%"
                  px={{ base: 4, lg: 0 }}
                >
                  <NewEventDetailBody fragmentRef={data.node} />
                </Box>

                {/* Sign Up section + Attendees (both mobile and desktop) */}
                <Box
                  width={{ base: "100%", lg: "auto" }}
                  pt={{ base: 6, lg: 6 }}
                >
                  <VStack gap={4} flex={1}>
                    <EventDetailSignUpSection fragmentRef={data.node} />
                  </VStack>
                </Box>
              </Box>

              {/* RIGHT Column (desktop only): Event info + hosted by + capacity + description */}
              {/* Attendees are in the left column under the sign up card */}
              <Box
                flex={{ lg: 1 }}
                width={{ base: "100%", lg: "auto" }}
                visible={{
                  xs: false,
                  base: false,
                  sm: false,
                  md: false,
                  lg: true,
                }}
              >
                <Box width="100%">
                  <NewEventDetailBody fragmentRef={data.node} infoOnly />
                </Box>
              </Box>
            </HStack>
          </Box>
        </ScrollView>

        {event ? (
          <UpdateAttendeesGroupEventsModal
            defaultPaymentCardOnFileId={defaultPaymentCardOnFileId}
            eventId={event.id}
            fragmentRef={event}
            unknownGuestsAllowed={event.unknownGuestsAllowed || false}
          />
        ) : null}
      </Container>
    </Box>
  ) : null;
};
